import { FC, ReactElement, Dispatch, SetStateAction } from 'react';
import { Field, useForm } from 'react-final-form';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState } from 'draft-js';

import { Box, RFFTextInput, RFFReactSelect } from '@qwealth/qcore';
import { QTask, QTaskTag, SelectOption, selectUser, TASK_COMPLETED, toValues } from '@qwealth/qdata';
import { commonSubscription, DocumentUploader } from '@qwealth/kyc';

import { StyledEditorDraftWysiwyg } from 'components/common/EditorWrapper';
import { StyledLabel } from './AddTask.styled';
import { getOtherUsersTags, QTaskForm } from 'data/models/taskForm';
import { useAppSelector } from 'data/store';
import { selectPartnerTaskTags } from 'data/selectors/taskTagsSelectors';
// TODO when qdata is updated, import this from there
import { TASK_TYPE } from 'data/helpers/taskHelper';
import { isEmpty } from 'lodash';
import { getTagKeys } from 'data/helpers/taskHelper';

interface Props {
  task: Partial<QTask> | null;
  editorState: EditorState | undefined;
  formValues: QTaskForm;
  setEditorState: Dispatch<SetStateAction<EditorState>>;
  taskAttachmentsPath: string;
}

const MainSectionAddAndEditMode: FC<Props> = ({ task, editorState, formValues, setEditorState, taskAttachmentsPath }): ReactElement => {
  const workflowTags: string[] = formValues?.workflowTags || [];
  const taskTagsOptions = useAppSelector(selectPartnerTaskTags)
    .map(tag => tag.key)
    .filter(tagKey => !workflowTags.includes(tagKey));

  const user = useAppSelector(selectUser);
  const form = useForm<QTaskForm>();
  const tagsFromOtherUsers = getOtherUsersTags(formValues?.taskTags, user.partnerID);
  const isTaskCompleted = formValues?.status === TASK_COMPLETED;

  return (
    <Box mb="largest" display="flex" flexDirection="column" gap="default" flexShrink={1} flexGrow={2} flexBasis="500px" minWidth="285px">
      <Box display="flex" flexDirection="column">
        <RFFTextInput
          name="title"
          label="Title*"
          subscription={commonSubscription}
          inputFieldProps={{
            disabled: isTaskCompleted,
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column">
        <RFFReactSelect
          label="Type"
          name="taskType"
          subscription={commonSubscription}
          inputFieldProps={{
            options: Object.values(TASK_TYPE),
            isDisabled: isTaskCompleted,
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap="small">
        <Box display="flex" flexDirection="column">
          <RFFReactSelect
            label="Tags"
            name="tags"
            subscription={commonSubscription}
            inputFieldProps={{
              options: taskTagsOptions,
              isMulti: true,
              isDisabled: isTaskCompleted,
              onChange: (selectedOptions: SelectOption<string>[]) => {
                const mapOptionsToTaskTagsFormat: QTaskTag[] = toValues(selectedOptions).map(o => ({
                  partnerId: user.partnerID,
                  key: o,
                }));
                form.change('taskTags', [...mapOptionsToTaskTagsFormat, ...tagsFromOtherUsers]);
              },
            }}
          />
        </Box>
        {!isEmpty(workflowTags) && (
          <Box color="gray.1" fontStyle="italic">
            <strong>Tags included by the Workflow: </strong> {workflowTags.join(', ')}
          </Box>
        )}
      </Box>

      <Box display="flex" flexDirection="column" mb="default" style={{ zIndex: 0 }}>
        <StyledLabel>Details*</StyledLabel>
        <Field name="details" subscription={commonSubscription}>
          {({ meta, input }) => (
            <StyledEditorDraftWysiwyg height={415} hasError={!!meta.error && meta.touched}>
              <Editor
                readOnly={isTaskCompleted}
                editorState={editorState}
                handlePastedText={() => false}
                onEditorStateChange={editorState => {
                  setEditorState(editorState);
                  input.onChange((Math.random() + 1).toString(36).substring(7));
                }}
                onBlur={() => {
                  input.onBlur();
                }}
                editorClassName="editor-class "
                wrapperClassName="wrapper-class"
                spellCheck
              />
              {meta.error && meta.touched && <Box color="error">{meta.error}</Box>}
            </StyledEditorDraftWysiwyg>
          )}
        </Field>
      </Box>

      <DocumentUploader title="Task Attachments" label="Task Attachments" editing={!isTaskCompleted} isMulti directPath={taskAttachmentsPath} />
    </Box>
  );
};

export default MainSectionAddAndEditMode;
